/**
 * @file Handles all configurations for the homepage tools.
 * @author Phillip Kühn
 */
import { type HptSessionStorageSettings, LOCAL_STORAGE_SETTINGS, getSessionStorage } from '../hooks/use-storage';
import checkUndefinedNullOrEmpty from '../utils/tools/checkUndefinedNullOrEmpty';
import merge from '../utils/tools/merge';
import configAhdWeb from './_ahd-web';
import configDefault from './_defaultValues';
import { mapConfigs } from './mapConfigs';
import { mapHosts } from './mapHosts';
import type { ConfigName } from './names';
import { type ConfigObject, DebugLevel } from './types';

/**
 * Function to get array of href-variants.
 *
 * @function
 * @param {string} href - Host adress without www.
 * @param {boolean} dev - get dev hrefs.
 * @return {string}
 */
function getHostVariants(href: string | null, dev = false): string[] {
    let returnArray: string[] = [];

    if (href === 'local' && dev === true) {
        returnArray = returnArray.concat(
            getHostVariants('localhost', true),
            getHostVariants('192.168.100.22', true),
            getHostVariants('fleet-dev.autohaus-digital.de', true),
            getHostVariants('fleet-next.autohaus-digital.de', true),
            getHostVariants('fleet-pre-release.autohaus-digital.de', true),
            getHostVariants('fleet-test.autohaus-digital.de', true)
        );
    } else if (href !== 'local') {
        returnArray.push(href);
        const hrefNoTopLevelDomain = href.replace(/\.[^.]{2,3}$/g, '');
        if (dev === false && !checkUndefinedNullOrEmpty(hrefNoTopLevelDomain)) {
            returnArray.push(hrefNoTopLevelDomain + '.autohaus-digital.de');
            returnArray.push(hrefNoTopLevelDomain + '-dev.autohaus-digital.de');
            returnArray.push(hrefNoTopLevelDomain + '-next.autohaus-digital.de');
            returnArray.push(hrefNoTopLevelDomain + '-pre-release.autohaus-digital.de');
            returnArray.push(hrefNoTopLevelDomain + '-test.autohaus-digital.de');
            returnArray.push(hrefNoTopLevelDomain + '.devbite.me');
            returnArray.push(hrefNoTopLevelDomain + '.devbite.de');
        }
    }

    return returnArray;
}

/**
 * Function to get array of href-variants.
 *
 * @function
 * @param {string} expectedHost - Host adress without www.
 * @param {boolean} dev - get dev hrefs.
 * @return {string}
 */
function checkHost(expectedHost: string | null, dev = false, locHost?: string): boolean {
    const hostsToFit = getHostVariants(expectedHost, dev);
    const check = hostsToFit?.some((host) => {
        const locationHost = locHost || (location.pathname.startsWith('/install') ? location.search.replace('?host=', '') : location.host);
        return locationHost.replace(/:[0-9]{1,5}\//g, '/').includes(host);
    });
    return check;
}

/**
 * Function to get configuration according to the location.
 *
 * @function
 * @param {object} additionalConfig - additional config from html element.
 * @return {ConfigObject}
 */
async function sConfigs(locHost: string, additionalConfig: Partial<ConfigObject> = {}): Promise<ConfigObject> {
    const configKeys = Object.keys(mapHosts) as ConfigName[];
    const configKey = configKeys?.find((c) => mapHosts?.[c]?.hosts?.some((host) => checkHost(host, mapHosts?.[c]?.isDev, locHost)));
    const config = await mapConfigs(configKey);

    const sessionStorage: HptSessionStorageSettings = getSessionStorage<HptSessionStorageSettings>(LOCAL_STORAGE_SETTINGS);

    const configObject: ConfigObject = merge({}, configDefault, config, config?.general?.ahdWeb === true ? configAhdWeb : {}, additionalConfig, {
        general: { debug: sessionStorage?.debug || DebugLevel.NONE },
    });

    return configObject;
}

export default sConfigs;
